// src/context/CartContext.js
import React, { createContext, useContext, useState, useMemo } from "react";
import { LanguageContext } from "./LanguageContext";
import { useEffect } from "react";
import { apiRequest } from "../services/api";
import { getMyWishlist, toggleWishlistApi } from "../services/wishlist.api";


const CartContext = createContext();

export function CartProvider({ children }) {
  const { lang } = useContext(LanguageContext);
  
  const [cartItems, setCartItems] = useState([]);
  const [sessionToken, setSessionToken] = useState(
     localStorage.getItem("esill-session")
  );

  const [authToken, setAuthToken] = useState(
  localStorage.getItem("esill-token")
);

const clearCart = () => {
  setCartItems([]);
};

  // WISHLIST
  const [wishlist, setWishlist] = useState([]);

  // COUPON
  const [couponCode, setCouponCode] = useState("");
  const [discountValue, setDiscountValue] = useState(0);

  // UI STATES (drawer + toast)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [addedToast, setAddedToast] = useState(null); // { nameEN, nameAR }
  
  useEffect(() => {
  const syncAuth = () => {
    setAuthToken(localStorage.getItem("esill-token"));
    setSessionToken(localStorage.getItem("esill-session"));
  };

  // initial sync
  syncAuth();

  // listen to custom auth change event
  window.addEventListener("auth-changed", syncAuth);

  return () => window.removeEventListener("auth-changed", syncAuth);
}, []);




 useEffect(() => {
  if (!authToken) {
    setWishlist([]); // clear wishlist on logout
    return;
  }

  const loadWishlist = async () => {
    try {
      const data = await getMyWishlist();
      setWishlist(
        data.map((p) => ({
          id: p.id,
          nameEN: p.name_en,
          nameAR: p.name_ar,
          price: p.sale_percent
            ? Math.round(p.base_price * (1 - p.sale_percent / 100))
            : p.base_price,
          image: p.image || "/images/placeholder-product.jpg",
        }))
      );
    } catch (err) {
  console.error("Failed to load wishlist", err);

  if (
    err.message === "Invalid token" ||
    err.message === "Unauthorized" ||
    err.status === 401
  ) {
    localStorage.removeItem("esill-token");
    localStorage.removeItem("esill-user");
    setAuthToken(null);
    setWishlist([]);
    window.dispatchEvent(new Event("auth-changed"));
  }
}
  };

  loadWishlist();
}, [authToken]); // 👈 THIS IS THE FIX


    // ---- CART OPERATIONS ----
  const loadCart = async (tokenOverride) => {
    try {
     const headers = {};

if (authToken) {
  headers["Authorization"] = `Bearer ${authToken}`;
}

if (sessionToken) {
  headers["x-session-token"] = sessionToken;
}

const API_URL =
  process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const res = await fetch(`${API_URL}/cart`, { headers });
      const data = await res.json();
if (!res.ok) {
  console.error("Cart API error:", data);
  return;
}
      if (data.session_token && !sessionToken) {
        localStorage.setItem("esill-session", data.session_token);
        setSessionToken(data.session_token);
      }

      setCartItems(
  (data.items || []).map((it) => {
    const basePrice = Number(it.base_price) || 0;
    const extraPrice = Number(it.extra_price) || 0;

    const finalBase =
      it.on_sale && Number(it.sale_percent) > 0
        ? Math.round(basePrice * (1 - it.sale_percent / 100))
        : basePrice;

    return {
  id: it.id,
  product_id: it.product_id,
  variant_id: it.variant_id,

  slug: it.seo_slug, // ✅ THIS IS THE FIX
  public_id: it.public_id,

  nameEN: it.name_en,
  nameAR: it.name_ar,
  price: finalBase + extraPrice,
  qty: it.qty,
  image: it.image || "/images/placeholder-product.jpg",
  size: it.size,
  color: it.color_name,
  color_hex: it.color_hex || "#000000",
  stock_qty: it.stock_qty,
};
  })
);


    } catch (err) {
      console.error("Failed to load cart", err);
    }
  };

  // initial load
  // 🔄 reload cart when auth token changes (login / logout)
useEffect(() => {
  loadCart();
}, [authToken, sessionToken]);


  const addToCart = async (product, qty = 1) => {
    try {
      // accept both shapes:
      // - {id, variant_id} OR {product_id, variant_id}
      const productId = product.product_id ?? product.id;
      const variantId = product.variant_id;

      await apiRequest("/cart/items", {
        method: "POST",
        headers:
  !authToken && sessionToken
    ? { "x-session-token": sessionToken }
    : {},

        body: JSON.stringify({
          product_id: productId,
          variant_id: variantId,
          qty,
        }),
      });

      await loadCart();
    } catch (err) {
      console.error(err);
    }
  };

  const removeFromCart = async (id) => {
    try {
      await apiRequest(`/cart/items/${id}`, {
        method: "DELETE",
        headers:
  !authToken && sessionToken
    ? { "x-session-token": sessionToken }
    : {},

      });

      await loadCart();
    } catch (err) {
      console.error(err);
    }
  };

  const changeQty = async (id, delta) => {
    const item = cartItems.find((x) => x.id === id);
    if (!item) return;

    const newQty = Math.max(1, item.qty + delta);

    try {
      await apiRequest(`/cart/items/${id}`, {
        method: "PUT",
        headers:
  !authToken && sessionToken
    ? { "x-session-token": sessionToken }
    : {},

        body: JSON.stringify({ qty: newQty }),
      });

      await loadCart();
    } catch (err) {
      console.error(err);
    }
  };


  // ---- WISHLIST ----
  const isInWishlist = (id) => wishlist.some((item) => item.id === id);

 const toggleWishlist = async (item) => {
  // 🔁 SYNC MODE (used by WishlistPage)
  if (item.__sync) {
    setWishlist(
      item.items.map((p) => ({
        id: p.id,
        nameEN: p.name_en,
        nameAR: p.name_ar,
        price: p.sale_percent
          ? Math.round(p.base_price * (1 - p.sale_percent / 100))
          : p.base_price,
        image: p.image || "/images/placeholder-product.jpg",
      }))
    );
    return;
  }

  const token = localStorage.getItem("esill-token");

  if (token) {
    try {
      await toggleWishlistApi(item.id);

      setWishlist((prev) => {
        const exists = prev.some((w) => w.id === item.id);
        return exists
          ? prev.filter((w) => w.id !== item.id)
          : [...prev, item];
      });
    } catch (err) {
      console.error("Wishlist API error", err);
    }
    return;
  }

  // guest
  setWishlist((prev) => {
    const exists = prev.some((w) => w.id === item.id);
    return exists
      ? prev.filter((w) => w.id !== item.id)
      : [...prev, item];
  });
};



  // ---- COUPON LOGIC ----
  const applyCoupon = (code) => {
    const trimmed = code.trim().toUpperCase();
    setCouponCode(trimmed);

    // Simple demo rule
    if (trimmed === "ESILL10") {
      // 10% off subtotal
      setDiscountValue((prev) => prev); // will be recomputed from subtotal below
    } else {
      setDiscountValue(0);
    }
  };

  // PRICES
  const subtotal = useMemo(
    () => cartItems.reduce((sum, item) => sum + item.price * item.qty, 0),
    [cartItems]
  );

  const computedDiscount = useMemo(() => {
    if (couponCode === "ESILL10") {
      return Math.round(subtotal * 0.1);
    }
    return 0;
  }, [couponCode, subtotal]);

  const total = subtotal - computedDiscount;

  // ---- DRAWER ----
  const openDrawer = () => setIsDrawerOpen(true);
  const closeDrawer = () => setIsDrawerOpen(false);

  const value = {
    cartItems,
    wishlist,
    isInWishlist,
    toggleWishlist,
    addToCart,
    removeFromCart,
    changeQty,
    clearCart,
    couponCode,
    applyCoupon,
    subtotal,
    discount: computedDiscount,
    total,
    isDrawerOpen,
    openDrawer,
    closeDrawer,
    addedToast,
    lang,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  return useContext(CartContext);
}
